import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { OrderTypeEnum } from '@flipster/apis/types';
import { putOpenPositionApi } from '@flipster/apis/v2/trade';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { refPositionTimes } from '@flipster/r230522/original/server-time';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { tradeModeBunja } from '../../../trade-mode/state';
import { orderAttributionAtom } from '../../order-attribution';
import { useSnapshotSpec } from '../../snapshot/use-snapshot-spec';
import { buildTradeRequestBase } from '../base-params';
import { useCancelPendingOrder } from '../cancel-order/use-cancel-order';

export interface UseClosePositionOptions {
  onMarketCloseComplete?: () => void;
  onLimitCloseComplete?: () => void;
  /** Sub-account ID for copy trading. If omitted, treated as main account. */
  accountId?: number;
}

/**
 * 포지션 종료(부분/전체, market/limit) + pending 취소 액션 (구 position-close-modal/use-close-position).
 * useMutation(훅) 형태 유지 — 소비자(행/모달)마다 인스턴스가 생겨 per-instance 로딩이 보존된다.
 * 토스트 매트릭스 보존: IOC 부분체결 에러 토스트 / OneWay+메인계정 무토스트(서버 alert가 대신) /
 * partial vs full 성공 토스트 / limit 접수 토스트. 에러는 스낵바 후 swallow(undefined 반환).
 */
export const useClosePosition = (options?: UseClosePositionOptions) => {
  const { onMarketCloseComplete, onLimitCloseComplete, accountId } = options ?? {};
  const t = useTranslations();
  const transPositionClose = useTranslations('Position_close.r250401');
  const { tradeModeAtom } = useBunja(tradeModeBunja);
  const tradeMode = useAtomValue(tradeModeAtom);
  const attribution = useAtomValue(orderAttributionAtom);

  const { instrumentsKeyBySymbol } = useSnapshotSpec();

  const { isCancelLoading, cancelPosition } = useCancelPendingOrder({ accountId });

  // partial close와 full close 공용
  const { isPending: isClosingLoading, mutateAsync: closePosition } = useMutation({
    mutationFn: async ({
      p,
      newSize,
      orderType,
      price,
    }: {
      p: OpenPosition | LegacyOpenPosition;
      newSize: string;
      orderType: 'market' | 'limit';
      price: string;
    }) => {
      try {
        if (!tradeMode) throw new Error('Unexpected missing tradeMode');
        const symbolInfo = instrumentsKeyBySymbol?.[p.symbol];

        const { data } = await putOpenPositionApi(p.symbol, p.slot, {
          ...buildTradeRequestBase(attribution),
          size: newSize,
          price,
          ...refPositionTimes[p.symbol],
          orderType: orderType === 'limit' ? OrderTypeEnum['Limit'] : OrderTypeEnum['Market'],
          accountId,
        });

        switch (orderType) {
          case 'market': {
            onMarketCloseComplete?.();

            const isIoc =
              data.position?.remainingPositionSize &&
              Num(newSize).abs().isLessThan(Num(data.position.remainingPositionSize).abs());

            if (isIoc) {
              const successAmount = BigNumber.max(Num(data.position?.size || '0').abs(), 0);
              const remainingAmount = Num(data.position?.remainingPositionSize || '0').abs();

              snackbar.error({
                message: t('Trade.r251119.amount_symbol_closed_at_amount_remains_try_again_toast', {
                  amountSymbol: `${numberFormatter(successAmount, {
                    precision: symbolInfo?.tradeQtyPrecision,
                    toFixed: true,
                  })} ${symbolInfo?.positionUnit}`,
                  amount: numberFormatter(data.position?.closePrice || '0', {
                    precision: symbolInfo?.pricePrecision,
                    toFixed: true,
                  }),
                  amountSymbolRemaining: `${numberFormatter(remainingAmount, {
                    precision: symbolInfo?.tradeQtyPrecision,
                    toFixed: true,
                  })} ${symbolInfo?.positionUnit}`,
                }),
              });
              return { closedPosition: data, isIoc: true };
            }

            // one way인 경우에는 서버에서 메시지를 내려주기 때문에, 따로 snackbar를 표시하지 않는다.
            // copy trading의 경우에는 private/alerts를 통해서 메시지가 오지 않기 때문에, oneway라도 snackbar를 표시한다.
            if (tradeMode === 'OneWay' && accountId == null) break;

            const symbolName = symbolInfo?.displayShortName;
            const isPartialClose = Num(data.position?.remainingPositionSize || 0)
              .abs()
              .isGreaterThan(0);

            if (isPartialClose) {
              snackbar.success({
                message: transPositionClose('Position_partially_closed_toast', { symbol: symbolName }),
              });
              break;
            }

            snackbar.success({ message: transPositionClose('Position_fully_closed_toast', { symbol: symbolName }) });
            break;
          }
          case 'limit': {
            onLimitCloseComplete?.();
            snackbar.success({ message: t('Trade.r251111.Your_limit_close_order_is_placed') });
            break;
          }
          default:
            break;
        }
        return { closedPosition: data, isIoc: false };
      } catch (error) {
        snackbar.error({ message: getErrorMessage(error) });
      }
    },
  });

  const { isPending: isFullCloseLoading, mutateAsync: closeFullPosition } = useMutation({
    mutationFn: async ({ position: p }: { position: OpenPosition | LegacyOpenPosition }) => {
      const response = await closePosition({
        p,
        newSize: '0',
        orderType: 'market',
        price: (p.side === 'Long' ? p.bidPrice : p.askPrice).toString(),
      });
      return response?.closedPosition;
    },
  });

  return {
    cancelPosition,
    closePosition,
    closeFullPosition,
    isCancelLoading,
    isCloseLoading: isFullCloseLoading || isClosingLoading,
  };
};
